import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { getAgentById } from "./fhpService";
import {
  extractPublicSiteSeedFromAgent,
  normalizePublicAgentId,
} from "../features/vacationPackagesPublic/publicSiteIdentity";

// Public-site bootstrap can include multiple DB/entitlements reads.
// Keep timeout above typical p95 to avoid false "site unavailable" states.
const PUBLIC_SITE_METADATA_TIMEOUT_MS = 15000;

function buildPublicSiteTokenUrl(token) {
  return resolveApiUrl(`/api/public-sites/by-token/${encodeURIComponent(token)}`);
}

function normalizePublicSiteToken(siteToken) {
  return String(siteToken || "").trim();
}

function isNumericSiteId(value) {
  return /^\d+$/.test(String(value || "").trim());
}

export async function fetchPublicSiteIdentity(siteToken, options = {}) {
  const safeSiteToken = normalizePublicSiteToken(siteToken);
  const byTokenUrl = buildPublicSiteTokenUrl(safeSiteToken);
  if (!safeSiteToken) {
    return { status: 400, data: { success: false, error: { code: "MISSING_SITE_TOKEN" } } };
  }
  if (isNumericSiteId(safeSiteToken)) {
    return {
      status: 400,
      data: {
        success: false,
        error: {
          code: "NUMERIC_SITE_ID_NOT_ALLOWED",
          message: "Public site must be loaded by publicSiteToken, not numeric id.",
        },
      },
    };
  }

  return axios.get(byTokenUrl, {
    timeout: PUBLIC_SITE_METADATA_TIMEOUT_MS,
    validateStatus: () => true,
    signal: options.signal,
  });
}

export async function fetchPublicSiteSlider(siteToken, options = {}) {
  const safeSiteToken = normalizePublicSiteToken(siteToken);
  if (!safeSiteToken) {
    return { status: 400, data: { success: false, error: { code: "MISSING_SITE_TOKEN" } } };
  }
  if (isNumericSiteId(safeSiteToken)) {
    return {
      status: 400,
      data: {
        success: false,
        error: {
          code: "NUMERIC_SITE_ID_NOT_ALLOWED",
          message: "Public site must be loaded by publicSiteToken, not numeric id.",
        },
      },
    };
  }
  return axios.get(`${buildPublicSiteTokenUrl(safeSiteToken)}/slider`, {
    timeout: PUBLIC_SITE_METADATA_TIMEOUT_MS,
    validateStatus: () => true,
    signal: options.signal,
  });
}

export async function fetchPublicSiteDestinations(siteToken, options = {}) {
  const safeSiteToken = normalizePublicSiteToken(siteToken);
  if (!safeSiteToken) {
    return { status: 400, data: { success: false, error: { code: "MISSING_SITE_TOKEN" } } };
  }
  if (isNumericSiteId(safeSiteToken)) {
    return {
      status: 400,
      data: {
        success: false,
        error: {
          code: "NUMERIC_SITE_ID_NOT_ALLOWED",
          message: "Public site must be loaded by publicSiteToken, not numeric id.",
        },
      },
    };
  }
  return axios.get(`${buildPublicSiteTokenUrl(safeSiteToken)}/destinations`, {
    timeout: PUBLIC_SITE_METADATA_TIMEOUT_MS,
    validateStatus: () => true,
    signal: options.signal,
  });
}

export async function fetchPublicSiteDestinationBySlug(siteToken, slug, options = {}) {
  const safeSiteToken = normalizePublicSiteToken(siteToken);
  const safeSlug = String(slug || "").trim();
  if (!safeSiteToken || !safeSlug) {
    return {
      status: 400,
      data: {
        success: false,
        error: { code: !safeSiteToken ? "MISSING_SITE_TOKEN" : "MISSING_SLUG" },
      },
    };
  }
  if (isNumericSiteId(safeSiteToken)) {
    return {
      status: 400,
      data: {
        success: false,
        error: {
          code: "NUMERIC_SITE_ID_NOT_ALLOWED",
          message: "Public site must be loaded by publicSiteToken, not numeric id.",
        },
      },
    };
  }
  return axios.get(`${buildPublicSiteTokenUrl(safeSiteToken)}/destinations/${encodeURIComponent(safeSlug)}`, {
    timeout: PUBLIC_SITE_METADATA_TIMEOUT_MS,
    validateStatus: () => true,
    signal: options.signal,
  });
}

export async function resolvePublicSiteRefByAgentId(agentId) {
  const normalizedAgentId = normalizePublicAgentId(agentId);
  if (!normalizedAgentId) {
    return {
      success: false,
      error: { code: "INVALID_AGENT_ID", message: "agentId must be numeric." },
    };
  }
  try {
    const res = await getAgentById(normalizedAgentId);
    const agent = res?.data?.data;
    const seed = extractPublicSiteSeedFromAgent(agent);
    if (!seed?.siteRef) {
      return {
        success: false,
        error: {
          code: "AGENT_PUBLIC_SITE_TOKEN_NOT_FOUND",
          message: "Agent does not expose a public site token/slug.",
        },
      };
    }
    return {
      success: true,
      siteRef: seed.siteRef,
      resolvedAgentId: seed.resolvedAgentId || normalizedAgentId,
      resolvedSlug: seed.resolvedSlug || null,
      agent,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: "AGENT_PUBLIC_SITE_RESOLVE_FAILED",
        message: error?.message || "Failed to resolve public site by agentId.",
      },
    };
  }
}
